import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Modal from 'react-bootstrap/Modal';
import StockCode from '../stock-code';
import Pricing from '../../fees-and-pricing-components/pricing';
import Contact from './invest-contact';
import dt, { useIsMobile } from '../../../../../shared/theme';
import { SSeparator, Title } from '../../fees-and-pricing-components/styles';

const StyledBody = styled(Modal.Body)`
  display: flex;
  padding: 56px 56px 47px;

  ${props => dt(props).theme.isXLarge && `
    padding: 56px 40px 47px;
  `};

  ${props => dt(props).theme.isMobile && `
    flex-direction: column;
    padding: 24px 20px;
    max-height: 100vh;
    overflow-y: auto;
    border-top: 1px solid #D7DFE7;
  `};

  * {
    box-sizing: content-box;
  }

  > div {
    &.instructions {
      width: 43.5%;
      padding-right: 8%;

      ${props => dt(props).theme.isXLarge && `
        width: 33%;
        margin-right: 6%;
      `};

      ${props => dt(props).theme.isMobile && `
        width: 100%;
      `};

      > p.main-title {
        font-size: 30px;
        display: inline-block;
        font-weight: 700;
        line-height: 130%;
        margin: 0;

        > span {
          margin-right: 12px;
        }
      }

      > p.content {
        margin-top: 16px;
        margin-bottom: 64px;
        font-size: 19px;
        line-height: 130%;
        color: #627484;
      }

      > div.contact {
        display: flex;
        flex-direction: column;

        > button {
          text-transform: none;
          font-size: 19px;
          max-width: 285px;
        }

        > p.external-link {
          font-size: 17px;
          text-align: center;
          margin-top: 24px;
          max-width: 285px;
          height: 20px;

          > a > span.icon-lw-external-link {
            margin: 0 6px 0 0;
          }
        }
      }
    }
  }

  div.details {
    width: 48.5%;
    margin-top: 4px;

    ${props => dt(props).theme.isXLarge && `
      width: 54%;
    `};

    ${props => dt(props).theme.isMobile && `
      width: 100%;
    `};

    > div > div > div {
      min-width: ${props => (props.theme.isXLarge ? '50%' : '100%')};
    }

    > div.info {
      display: flex;
      flex-wrap: wrap;
      padding-top: ${props => (props.theme.isMobile ? 0 : '16px')};

      > div {
        min-width: ${props => (props.theme.isXLarge ? '50%' : '100%')};
        display: flex;
        flex-direction: column;
        margin-bottom: 16px;

        > p.title {
          font: ${props => props.theme.fontWeightBold} 14px/120% ${props => props.theme.fontFamilyBody};
          color: ${props => props.theme.secondaryColor};
          letter-spacing: 0.1px;
          text-transform: uppercase;
          margin-bottom: 4px;
          display: flex;
          justify-content: flex-start;
          height: 16px;
        }

        > p.data {
          margin: 0;
        }
      }
    }
  }
`;

const STOCK_EXCHANGES = {
  ASX: {
    name: 'Australian Securities Exchange (ASX)',
    tradingHours: '10:00AM - 4:00PM (AEST)',
  },
  CBOE: {
    name: 'CBOE Australia',
    tradingHours: '10:00AM - 4:13PM (AEST)',
  },
};

export default function InvestModalBody({ fund, contact }) {
  const isMobile = useIsMobile();
  const exchangeName = STOCK_EXCHANGES[fund.stockExchange?.toUpperCase()]?.name;
  const tradingHours = STOCK_EXCHANGES[fund.stockExchange?.toUpperCase()]?.tradingHours;

  return (
    <StyledBody>
      <div className="instructions">
        <p className="main-title">
          <span>{fund.name}</span>
          {!!fund.stockCode && <StockCode code={fund.stockCode} exchange={fund.stockExchange} />}
        </p>

        <p className="content">Shares in this fund can be purchased on a stock exchange like other listed securities. Please consult your stock broker or financial advisor.</p>

        {!isMobile && <Contact id={fund.id} url={fund.fundPageUrl} contact={contact} />}
      </div>

      <div className="details">
        <Pricing fund={fund} isOnModal />

        <div className="info">
          <Title>Symbols and Trading Hours</Title>
          <SSeparator />

          <div>
            <p className="title">isin</p>
            <p className="data">{fund.isin}</p>
          </div>

          {!!fund.stockCode && (
          <div>
            <p className="title">stock code</p>
            <p className="data">{fund.stockCode.toUpperCase()}</p>
          </div>
          )}

          <div>
            <p className="title">exchange</p>
            <p className="data">{ exchangeName }</p>
          </div>

          <div>
            <p className="title">trading hours</p>
            <p className="data">{ tradingHours }</p>
          </div>
        </div>
      </div>
    </StyledBody>
  );
}

InvestModalBody.propTypes = {
  fund: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    isin: PropTypes.string.isRequired,
    stockCode: PropTypes.string,
    stockExchange: PropTypes.string,
    fundPageUrl: PropTypes.string,
  }).isRequired,
  contact: PropTypes.func,
};

InvestModalBody.defaultProps = { contact: null };
